<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { debounce } from '@chatwoot/utils';
import ProductsAPI from 'dashboard/api/products';
import { useCommercialGoalsStore } from 'dashboard/stores/commercialGoals';
import { GOAL_TYPES } from 'dashboard/components-next/CommercialGoals/useCommercialGoalFormat';
import {
  validateGoalForm,
  getPeriodShortcut,
} from 'dashboard/components-next/CommercialGoals/commercialGoalForm';

import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const emit = defineEmits(['saved']);

const { t } = useI18n();
const store = useStore();
const goalsStore = useCommercialGoalsStore();

const dialogRef = ref(null);
const goal = ref(null);
const form = ref({});
const errors = ref({});
const serverError = ref('');

const productOptions = ref([]);

const isEditing = computed(() => !!goal.value);
const isSaving = computed(
  () => goalsStore.uiFlags.creatingItem || goalsStore.uiFlags.updatingItem
);

const typeOptions = GOAL_TYPES.map(type => ({
  value: type,
  label: t(`COMMERCIAL_GOALS.TYPES.${type.toUpperCase()}`),
}));
const scopeOptions = ['team', 'user'].map(scope => ({
  value: scope,
  label: t(`COMMERCIAL_GOALS.SCOPE.${scope.toUpperCase()}`),
}));

const userOptions = computed(() => {
  const options = store.getters['agents/getAgents'].map(agent => ({
    value: agent.id,
    label: agent.name,
  }));
  const assignedUser = goal.value?.assignedUser;
  if (assignedUser && !options.some(({ value }) => value === assignedUser.id)) {
    options.push({ value: assignedUser.id, label: assignedUser.name });
  }
  return options;
});

const targetLabel = computed(
  () =>
    `${t('COMMERCIAL_GOALS.DIALOG.TARGET_LABEL')} (${t(
      `COMMERCIAL_GOALS.DIALOG.TARGET_UNIT.${form.value.goalType?.toUpperCase()}`
    )})`
);

const fieldError = key =>
  errors.value[key]
    ? t(`COMMERCIAL_GOALS.VALIDATION.${errors.value[key]}`)
    : '';

// Busca na API (o catálogo pode ter muitos itens); ao editar, mantém o produto da meta nas opções.
const loadProducts = async (query = '') => {
  try {
    const { data } = await ProductsAPI.list({ q: query || undefined, page: 1 });
    productOptions.value = (data.payload || []).map(product => ({
      value: product.id,
      label: product.name,
    }));
  } catch {
    productOptions.value = [];
  }
  const current = goal.value?.product;
  if (
    current &&
    !productOptions.value.some(({ value }) => value === current.id)
  ) {
    productOptions.value.push({ value: current.id, label: current.name });
  }
};
const searchProducts = debounce(loadProducts, 300);

const applyShortcut = shortcut => {
  Object.assign(form.value, getPeriodShortcut(shortcut));
};

const open = (goalToEdit = null) => {
  goal.value = goalToEdit;
  errors.value = {};
  serverError.value = '';
  form.value = {
    title: goalToEdit?.title ?? '',
    goalType: goalToEdit?.goalType ?? 'revenue',
    scopeType: goalToEdit?.scopeType ?? 'team',
    productId: goalToEdit?.productId ?? '',
    assignedUserIds: goalToEdit?.assignedUserId
      ? [goalToEdit.assignedUserId]
      : [],
    targetValue: goalToEdit?.targetValue ?? '',
    startDate: goalToEdit?.startDate ?? '',
    endDate: goalToEdit?.endDate ?? '',
  };
  store.dispatch('agents/get');
  loadProducts();
  dialogRef.value.open();
};

const buildPayload = () => {
  const { title, goalType, scopeType, assignedUserIds } = form.value;
  const attrs = {
    title: title.trim() || undefined,
    target_value: Number(form.value.targetValue),
    start_date: form.value.startDate,
    end_date: form.value.endDate,
  };
  if (isEditing.value) return { id: goal.value.id, ...attrs };

  return {
    ...attrs,
    goal_type: goalType,
    scope_type: scopeType,
    product_id: goalType === 'product_sales' ? form.value.productId : undefined,
    assigned_user_ids: scopeType === 'user' ? assignedUserIds : undefined,
  };
};

const save = async () => {
  errors.value = validateGoalForm(form.value, isEditing.value);
  if (Object.keys(errors.value).length) return;

  serverError.value = '';
  try {
    if (isEditing.value) await goalsStore.update(buildPayload());
    else await goalsStore.create(buildPayload());
    useAlert(
      t(
        isEditing.value
          ? 'COMMERCIAL_GOALS.MESSAGES.UPDATED'
          : 'COMMERCIAL_GOALS.MESSAGES.CREATED'
      )
    );
    dialogRef.value.close();
    emit('saved');
  } catch (error) {
    serverError.value =
      error.message || t('COMMERCIAL_GOALS.MESSAGES.SAVE_ERROR');
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="xl"
    :title="
      t(
        isEditing
          ? 'COMMERCIAL_GOALS.DIALOG.EDIT_TITLE'
          : 'COMMERCIAL_GOALS.DIALOG.CREATE_TITLE'
      )
    "
    :confirm-button-label="t('COMMERCIAL_GOALS.DIALOG.SAVE')"
    :is-loading="isSaving"
    @confirm="save"
  >
    <div class="flex flex-col gap-4">
      <div
        v-if="serverError"
        class="flex flex-col gap-1 p-3 text-sm border rounded-lg border-n-ruby-6 bg-n-ruby-2 text-n-ruby-11"
      >
        <span>{{ serverError }}</span>
      </div>

      <p v-if="isEditing" class="mb-0 text-xs text-n-slate-11">
        {{ t('COMMERCIAL_GOALS.DIALOG.LOCKED_HINT') }}
      </p>

      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div class="flex flex-col gap-1">
          <label class="text-heading-3 text-n-slate-12">
            {{ t('COMMERCIAL_GOALS.DIALOG.TYPE_LABEL') }}
          </label>
          <ComboBox
            v-model="form.goalType"
            :options="typeOptions"
            :allow-deselect="false"
            :disabled="isEditing"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-heading-3 text-n-slate-12">
            {{ t('COMMERCIAL_GOALS.DIALOG.SCOPE_LABEL') }}
          </label>
          <ComboBox
            v-model="form.scopeType"
            :options="scopeOptions"
            :allow-deselect="false"
            :disabled="isEditing"
          />
        </div>
      </div>

      <div v-if="form.goalType === 'product_sales'" class="flex flex-col gap-1">
        <label class="text-heading-3 text-n-slate-12">
          {{ t('COMMERCIAL_GOALS.DIALOG.PRODUCT_LABEL') }}
        </label>
        <ComboBox
          v-model="form.productId"
          use-api-results
          :allow-deselect="false"
          :options="productOptions"
          :disabled="isEditing"
          :placeholder="t('COMMERCIAL_GOALS.DIALOG.PRODUCT_PLACEHOLDER')"
          :search-placeholder="t('COMMERCIAL_GOALS.DIALOG.PRODUCT_SEARCH')"
          :empty-state="t('COMMERCIAL_GOALS.DIALOG.PRODUCT_EMPTY')"
          :has-error="!!errors.product"
          :message="fieldError('product')"
          @search="searchProducts"
        />
      </div>

      <div v-if="form.scopeType === 'user'" class="flex flex-col gap-1">
        <label class="text-heading-3 text-n-slate-12">
          {{ t('COMMERCIAL_GOALS.DIALOG.USERS_LABEL') }}
        </label>
        <TagMultiSelectComboBox
          v-model="form.assignedUserIds"
          :options="userOptions"
          :disabled="isEditing"
          :placeholder="t('COMMERCIAL_GOALS.DIALOG.USERS_PLACEHOLDER')"
          :search-placeholder="t('COMMERCIAL_GOALS.DIALOG.USERS_SEARCH')"
          :empty-state="t('COMMERCIAL_GOALS.DIALOG.USERS_EMPTY')"
          :has-error="!!errors.users"
          :message="fieldError('users')"
        />
      </div>

      <Input
        v-model="form.targetValue"
        type="number"
        min="0"
        :step="
          ['sales_count', 'product_sales'].includes(form.goalType) ? '1' : 'any'
        "
        :label="targetLabel"
        :message-type="errors.targetValue ? 'error' : 'info'"
        :message="
          fieldError('targetValue') ||
          (form.goalType === 'sales_time'
            ? t('COMMERCIAL_GOALS.DIALOG.TARGET_HINT_SALES_TIME')
            : '')
        "
      />

      <div class="flex flex-col gap-2">
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            v-model="form.startDate"
            type="date"
            :label="t('COMMERCIAL_GOALS.DIALOG.START_DATE')"
            :message-type="errors.dates ? 'error' : 'info'"
          />
          <Input
            v-model="form.endDate"
            type="date"
            :label="t('COMMERCIAL_GOALS.DIALOG.END_DATE')"
            :message-type="errors.dates ? 'error' : 'info'"
            :message="fieldError('dates')"
          />
        </div>
        <div class="flex flex-wrap gap-2">
          <Button
            v-for="shortcut in ['month', 'quarter', 'year']"
            :key="shortcut"
            type="button"
            variant="faded"
            color="slate"
            size="xs"
            :label="
              t(
                `COMMERCIAL_GOALS.DIALOG.SHORTCUTS.THIS_${shortcut.toUpperCase()}`
              )
            "
            @click="applyShortcut(shortcut)"
          />
        </div>
      </div>

      <Input
        v-model="form.title"
        :label="t('COMMERCIAL_GOALS.DIALOG.TITLE_LABEL')"
        :placeholder="t('COMMERCIAL_GOALS.DIALOG.TITLE_PLACEHOLDER')"
      />
    </div>
  </Dialog>
</template>
